import PropTypes from 'prop-types';
import Textarea from "components/form/Textarea/Textarea";
import { useState } from "react";
import Dialog from "components/ui/Dialog/Dialog";
import { useDialog } from "context/DialogContext";
import Button from "components/ui/Button/Button";
import useData from "hooks/useData";
import Input from "components/form/Input/Input";
import Interests from "components/ui/Interests/Interests";
import ImageUpload from "components/form/ImageUpload/ImageUpload";
import useEntity from "hooks/useEntity";
import BulletinTypes from "features/bulletins/components/BulletinTypes";
import Autocomplete from "components/form/Autocomplete/Autocomplete";
import { Link } from "react-router-dom";
import CheckProfileVisible from "pages/Profile/CheckProfileVisible";

const BulletinCreateUpdate = ({ id, groupId, categoryId, isMarketPlace, onCompleted }) => {
  const { hideDialog } = useDialog();
  const { postData, patchData } = useData();
  const [ saving, setSaving ] = useState(false);
  const [ error, setError ] = useState();
  const [ title, setTitle ] = useState('');
  const [ description, setDescription ] = useState('');
  const [ typeId, setTypeId ] = useState();
  const [ interests, setInterests ] = useState([]);
  const [ imageData, setImageData ] = useState();
  const [ location, setLocation ] = useState();
  const [ locationDescription, setLocationDescription ] = useState();

  const { entity, isLoading } = useEntity(id, 'bulletin');

  // Set default values once the entity data is loaded.
  if (id && entity && !title) {
    setTitle(entity.title && entity.title.value);
    setDescription(entity.field_description && entity.field_description.value);
    setTypeId(entity.field_bulletin_type && entity.field_bulletin_type.id);
    setInterests(entity.field_interests ? entity.field_interests.map(item => item.id) : []);
    setLocation(entity.field_location ? entity.field_location.value : null);
    setLocationDescription(entity.field_location_description ? entity.field_location_description.value : null);
  }

  const handleRenderLocation = (result) => {
    return result.description;
  }

  const handleSelectLocation = (item) => {
    setLocation(item?.wkt);
    setLocationDescription(item?.description);
  }

  const onSave = async () => {
    setError(null);

    if (!title) {
      setError('Titel is een verplicht veld');
      return;
    }

    if (!description) {
      setError('Bericht is een verplicht veld');
      return;
    }

    if (!typeId) {
      setError('U heeft nog geen categorie geselecteerd');
      return;
    }

    setSaving(true);

    let saveData = {
      ...entity,
      title: { value: title },
      field_description: { value: description },
      field_bulletin_image: { target_id: imageData && imageData.fid[0].value },
      field_bulletin_type: { target_id: typeId },
      field_interests: interests.map(id => {
        return { target_id: id }
      }),
      field_location: { value: location },
      field_location_description: { value: location ? locationDescription : null },
    };

    if (groupId) {
      saveData.field_group = { target_id: groupId };
    }

    let response;
    if (entity && entity.id) {
      response = await patchData(entity.id, 'bulletin', saveData, false);
    } else {
      response = await postData('rest/bulletin', saveData, false);
    }

    const result = await response?.json();

    if (response?.status === 200) {
      hideDialog();
      if (onCompleted) {
        onCompleted(result);
      }
      return;
    }

    setError(result?.message || `Het opslaan is mislukt (${ response?.status })`);

    setSaving(false);
  };

  const isGroupBulletin = groupId || entity?.field_group?.id;

  const form = (
    <>
      <div className={ `dialog__row` }>
        <h3>Titel</h3>
        <Input type={ `text` } onChange={ event => setTitle(event.target.value) } value={ title } autoFocus/>
      </div>

      <div className={ `dialog__row` }>
        <h3>Bericht</h3>
        <Textarea onChange={ event => setDescription(event.target.value) } value={ description }/>
      </div>


      <div className={ `dialog__row` }>
        <h3>Selecteer een categorie</h3>
        <BulletinTypes defaultValue={ parseInt(typeId) }
                       onChange={ setTypeId }
                       categoryParentId={ categoryId || entity?.bulletin_parent_type?.id }
        />
      </div>

      {
        !isGroupBulletin &&
        <>
          <div className={ `dialog__row` }>
            <h3>Om welke postcode of plaats gaat het? <span className={ 'heading-subtext' }>(niet verplicht)</span></h3>
            <Autocomplete endpoint={ `rest/geocode` }
                          value={ locationDescription && { description: locationDescription } }
                          placeholder={ `Wat is uw postcode of plaats?` }
                          onRenderResult={ handleRenderLocation }
                          onSelect={ handleSelectLocation }/>
          </div>

          { !isMarketPlace &&
            <div className={ `dialog__row` }>
              <h3>Kies bij dit bericht passende interesses</h3>
              <Interests selection={ interests } onSelectionUpdated={ setInterests }/>
            </div>
          }
        </>
      }

      <div className={ `dialog__row` }>
        <ImageUpload title={ `Foto bij dit bericht` }
                     subtitle={ `(niet verplicht)` }
                     type="node"
                     bundle="bulletin"
                     field="field_bulletin_image"
                     currentData={ imageData }
                     currentValue={ entity && entity.field_bulletin_image && entity.field_bulletin_image.value }
                     onUploaded={ setImageData }/>
      </div>

      <div className={ 'dialog__row dialog__buttons dialog__buttons--end' }>
        <Button onClick={ onSave } variant={ 'secondary' }>Opslaan</Button>
      </div>

      <div className={ `dialog__footnote` }>
        { !isGroupBulletin ? `Dit bericht is openbaar en zichtbaar voor alle Trefpunt leden.` : `` }
        Houd u aan de <Link to={ '/huisregels' } target="_blank" rel="noopener noreferrer">huisregels</Link> bij
        het plaatsen van een bericht.
      </div>
    </>
  );

  let entityLabel = `Prikbordbericht`;
  if (isMarketPlace) {
    entityLabel = `Vraag & aanbod`;
  }
  else if (isGroupBulletin) {
    entityLabel = `Groepsbericht`;
  }

  return (
    <Dialog title={ entity && entity.id ? entityLabel + ' aanpassen' : entityLabel + ' plaatsen' }
            loading={ isLoading || saving }
            errorMessage={ error }
            warnOnClose={ true }>
      <CheckProfileVisible>
        { form }
      </CheckProfileVisible>
    </Dialog>
  )
}

BulletinCreateUpdate.propTypes = {
  id: PropTypes.number,
  groupId: PropTypes.number,
  isMarketPlace: PropTypes.bool,
  onCompleted: PropTypes.func,
}

export default BulletinCreateUpdate;
